import { gameState, type GameState } from '../state/game-state';
import { audio } from '../audio/shockwave-sfx';
import { CONFIG } from '../config';

export class HUD {
  private root: HTMLElement;
  private corner: HTMLDivElement;
  private shieldEl: HTMLDivElement;
  private weaponsEl: HTMLDivElement;
  private scoreEl: HTMLDivElement;
  private elapsedEl: HTMLDivElement;
  private timerEl: HTMLDivElement;
  private remainingEl: HTMLDivElement;
  private tempoEl: HTMLDivElement;
  private tempoPulseEl: HTMLDivElement;
  private tempoBpmEl: HTMLDivElement;
  private tempoLabelEl: HTMLDivElement;
  private hintEl: HTMLDivElement;
  private muteBtn: HTMLButtonElement;
  private hintShown: Record<string, boolean> = { fist: false, scissors: false, open_palm: false, run_done: false };
  private hintTimer: number | null = null;

  constructor(parent: HTMLElement) {
    this.root = document.createElement('div');
    this.root.className = 'hud';
    parent.appendChild(this.root);

    this.corner = this.makeCorner('hud-tl', '腐蚀度', '0%', 'corruption');
    this.root.appendChild(this.corner);

    this.timerEl = this.makeCorner('hud-tm', '剩余时间', this.formatTime(CONFIG.run.timeLimitMs), 'timer');
    this.root.appendChild(this.timerEl);

    this.remainingEl = this.makeCorner('hud-tr', '还需击败', `${CONFIG.run.winDefeatCount} 只`, 'remaining');
    this.root.appendChild(this.remainingEl);

    this.tempoEl = document.createElement('div');
    this.tempoEl.className = 'hud-tempo';
    this.tempoEl.innerHTML = `
      <div class="hud-tempo-pulse"></div>
      <div class="hud-tempo-info">
        <div class="hud-tempo-bpm">BPM ${Math.round(CONFIG.run.startBpm)}</div>
        <div class="hud-tempo-label">热身</div>
      </div>
    `;
    this.tempoPulseEl = this.tempoEl.querySelector('.hud-tempo-pulse') as HTMLDivElement;
    this.tempoBpmEl = this.tempoEl.querySelector('.hud-tempo-bpm') as HTMLDivElement;
    this.tempoLabelEl = this.tempoEl.querySelector('.hud-tempo-label') as HTMLDivElement;
    this.root.appendChild(this.tempoEl);

    this.shieldEl = this.makeCorner('hud-ml', '护盾', '就绪', 'shield');
    this.root.appendChild(this.shieldEl);

    this.weaponsEl = this.makeCorner('hud-mc', '兵器', '1 ⚔', 'weapons');
    this.root.appendChild(this.weaponsEl);

    this.scoreEl = this.makeCorner('hud-mr', '已击退', '0', 'score');
    this.root.appendChild(this.scoreEl);

    this.elapsedEl = document.createElement('div');
    this.elapsedEl.className = 'hud-center-bottom mono';
    this.elapsedEl.textContent = '';
    this.root.appendChild(this.elapsedEl);

    this.hintEl = document.createElement('div');
    this.hintEl.className = 'hud-hint';
    this.hintEl.textContent = '';
    this.root.appendChild(this.hintEl);

    this.muteBtn = document.createElement('button');
    this.muteBtn.className = 'hud-mute';
    this.muteBtn.textContent = '🔊';
    this.muteBtn.title = '按 M 静音';
    this.muteBtn.addEventListener('click', () => this.toggleMute());
    this.root.appendChild(this.muteBtn);

    gameState.subscribe((s) => this.render(s));

    setTimeout(() => this.showHint('出拳：击退恐龙'), 8000);
    setTimeout(() => this.showHint('剪刀手：推植物'), 11000);
    setTimeout(() => this.showHint('张手：展开护盾'), 14000);
  }

  private makeCorner(
    posClass: string,
    label: string,
    value: string,
    kind: 'corruption' | 'shield' | 'weapons' | 'score' | 'timer' | 'remaining'
  ): HTMLDivElement {
    const el = document.createElement('div');
    el.className = `hud-corner ${posClass} mono`;
    el.innerHTML = `<div class="lbl">${label}</div><div class="val">${value}</div>`;
    (el as unknown as { _kind: string })._kind = kind;
    return el;
  }

  private setCornerValue(el: HTMLDivElement, label: string, value: string, cls: string = ''): void {
    const posClass = ['hud-tl', 'hud-tm', 'hud-tr', 'hud-ml', 'hud-mc', 'hud-mr'].find((c) =>
      el.classList.contains(c)
    ) || 'hud-tl';
    el.className = `hud-corner ${posClass} mono ${cls}`;
    el.innerHTML = `<div class="lbl">${label}</div><div class="val">${value}</div>`;
  }

  private formatTime(ms: number): string {
    const total = Math.max(0, Math.round(ms / 1000));
    const mm = String(Math.floor(total / 60)).padStart(2, '0');
    const ss = String(total % 60).padStart(2, '0');
    return `${mm}:${ss}`;
  }

  showHint(text: string): void {
    this.hintEl.textContent = text;
    this.hintEl.classList.add('show');
    if (this.hintTimer !== null) {
      clearTimeout(this.hintTimer);
    }
    this.hintTimer = window.setTimeout(() => {
      this.hintEl.classList.remove('show');
    }, 3000);
  }

  onBeat(bpm: number, difficulty: number): void {
    if (this.tempoPulseEl) {
      this.tempoPulseEl.classList.remove('flash');
      void this.tempoPulseEl.offsetWidth;
      this.tempoPulseEl.classList.add('flash');
    }
    if (this.tempoBpmEl) {
      this.tempoBpmEl.textContent = `BPM ${Math.round(bpm)}`;
    }
    if (this.tempoLabelEl) {
      let label = '热身';
      if (difficulty >= 0.85) label = '极限';
      else if (difficulty >= 0.65) label = '困难';
      else if (difficulty >= 0.45) label = '紧张';
      else if (difficulty >= 0.25) label = '普通';
      this.tempoLabelEl.textContent = label;
      const cls =
        difficulty >= 0.65 ? 'hard' : difficulty >= 0.25 ? 'mid' : 'easy';
      this.tempoEl.className = `hud-tempo ${cls}`;
    }
  }

  showHintIfFirst(kind: 'fist' | 'scissors' | 'open_palm' | 'run_done'): void {
    if (this.hintShown[kind]) return;
    this.hintShown[kind] = true;
    if (kind === 'fist') this.showHint('出拳：击退恐龙');
    else if (kind === 'scissors') this.showHint('剪刀手：推植物');
    else if (kind === 'open_palm') this.showHint('张手：展开护盾');
  }

  private render(state: GameState): void {
    const corruptionPct = Math.round(state.corruption);
    this.setCornerValue(
      this.corner,
      '腐蚀度',
      `${corruptionPct}%${state.isRespawning ? ' · 重生中' : ''}`,
      state.isRespawning ? 'crit' : state.corruption >= 60 ? 'warn' : ''
    );

    const timeCls = state.isTimeUp
      ? 'crit'
      : state.timeLeftMs < 30000
        ? 'warn'
        : '';
    this.setCornerValue(this.timerEl, '剩余时间', this.formatTime(state.timeLeftMs), timeCls);

    const winTarget = CONFIG.run.winDefeatCount;
    const remainingCls =
      state.dinosaursRemaining === 0
        ? 'ok'
        : state.dinosaursRemaining <= 10
          ? 'warn'
          : '';
    const remainingText = state.dinosaursRemaining === 0 ? '已通关 ✓' : `${state.dinosaursRemaining} 只`;
    this.setCornerValue(
      this.remainingEl,
      '还需击败',
      remainingText,
      remainingCls
    );

    const now = performance.now();
    if (state.shieldActiveUntil > now) {
      const remaining = Math.max(0, Math.ceil((state.shieldActiveUntil - now) / 1000));
      this.setCornerValue(this.shieldEl, '护盾', `激活 ${remaining}s`, 'warn');
    } else {
      const cd = Math.max(
        0,
        Math.ceil((CONFIG.tongue.cooldownMs - (now - state.shieldLastTrigger)) / 1000)
      );
      this.setCornerValue(
        this.shieldEl,
        '护盾',
        cd > 0 ? `冷却 ${cd}s` : '就绪',
        cd > 0 ? '' : ''
      );
    }

    this.setCornerValue(
      this.weaponsEl,
      '兵器',
      `${state.weaponCount} ⚔`,
      state.corruption >= 60 ? 'warn' : ''
    );

    this.setCornerValue(
      this.scoreEl,
      '已击退',
      `${state.dinosaursDefeated} / ${CONFIG.run.winDefeatCount}`,
      state.dinosaursDefeated >= CONFIG.run.winDefeatCount - 10 ? 'warn' : ''
    );

    if (state.dinosaursRemaining > 0 && state.dinosaursRemaining <= 10 && !state.isTimeUp && !state.isDead) {
      this.showHint(`再击退 ${state.dinosaursRemaining} 只即可通关！`);
    }

    if (state.isTimeUp) {
      this.elapsedEl.textContent = `时间到 · ${this.formatTime(state.elapsedMs)}`;
      this.elapsedEl.classList.add('show');
    } else if (state.isRunComplete) {
      this.elapsedEl.textContent = `穿越完成 · 用时 ${this.formatTime(state.elapsedMs)}`;
      this.elapsedEl.classList.add('show');
    } else {
      this.elapsedEl.classList.remove('show');
    }

    if (state.corruption >= 40 && !this.hintShown.open_palm) {
      this.showHintIfFirst('open_palm');
    }

    this.muteBtn.textContent = state.isMuted ? '🔇' : '🔊';
    this.muteBtn.classList.toggle('muted', state.isMuted);
  }

  toggleMute(): void {
    gameState.toggleMute();
    audio.setMuted(gameState.get().isMuted);
  }
}
